import React, { useState } from 'react';
import { notifyError, notifySuccess } from '@/src/shared/utils/message';
import Input from '@/src/shared/components/Antd/Input';
import { empty } from '@/core/helpers';
import { useContactUs } from '@/src/data/general/useContactUs';
import { isValidEmail } from '@/helpers/validation';
import type { IContactUs } from '@/types/misc';
import backgroundImage from '../../../../../public/images/contact-us/new/form-bg.png';
import { TextInput } from './TextInput';
import styles from './index.module.scss';
import { Button } from '@/src/shared/components/Button';
import { Checkbox } from 'src/shared/components/Checkbox';
import { Typography } from '@/src/shared/components/Typography';

const { TextArea } = Input;

export function ContactForm() {
  const { mutateAsync: contactUs, isLoading } = useContactUs();
  const [form, setForm] = useState<IContactUs>({
    name: '',
    email: '',
    text: '',
    phone: '',
  });
  const [agreementIsChecked, setAgreementIsChecked] = useState<boolean>(false);

  const handleFormSubmit = async () => {
    if (
      empty(form.email) ||
      empty(form.phone) ||
      empty(form.name) ||
      empty(form.text)
    ) {
      notifyError('Name, Email, Phone and Message cannot be empty!');
      return;
    }
    if (!isValidEmail(form.email)) {
      notifyError('The Email is not valid');
      return;
    }

    try {
      await contactUs({
        text: form.text,
        email: form.email,
        phone: form.phone,
        source: 'contact-us',
        name: form.name,
      });

      notifySuccess('Your message sent successfully.');

      setForm({ ...form, name: '', email: '', text: '' });
    } catch (error) {
      notifyError('Your request encountered with a problem.');
    }
  };

  return (
    <section
      className={styles.container}
      style={{
        background: `url(${backgroundImage.src}) no-repeat center`,
        backgroundSize: 'cover',
      }}
    >
      <div className={styles.wrapper}>
        <div className={styles.form}>
          <form>
            <div>
              <h5 className={styles.touchTitle}>Get in touch !</h5>
              <h5 className={styles.quote}>
                Contact us for a quote, help or to join the team.
              </h5>
            </div>
            <div className={styles.formRow}>
              <div className={styles.field}>
                <TextInput
                  label="Name"
                  placeholder="Your Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className={styles.field}>
                <TextInput
                  label="Email Address"
                  placeholder="Email@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
            </div>
            <div className={styles.field}>
              <TextInput
                label="Phone"
                placeholder="Phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                fullWidth
              />
            </div>
            <div className={styles.field}>
              <label className={styles.inputLabel}>Message</label>
              <TextArea
                placeholder="Write your message ..."
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                required
                rows={7}
                style={{
                  backgroundColor: '#2979FF',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </div>
            <Checkbox
              id="agreement"
              checked={agreementIsChecked}
              classes={{
                checkbox: styles.checkbox,
              }}
              label={
                <div className={styles.checkboxLabel}>
                  <Typography
                    className={styles.terms}
                    variant="text"
                    size="sm"
                    fontWeight="medium"
                    tag="span"
                  >
                    {`I agree to VuSocial's `}
                    <a
                      href="https://www.roomvu.com/terms-of-service"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms
                    </a>{' '}
                  </Typography>
                  <Typography
                    className={styles.checkboxLabel}
                    variant="text"
                    size="xs"
                    fontWeight="medium"
                  >
                    I agree to receive communications by text message regarding
                    VuSocial Technologies Inc automated video content, lead
                    generation tools, and CRM support from VuSocial.You may opt
                    out by replying STOP or ask for more information by replying
                    HELP. Message frequency varies. Message and data rates may
                    apply.You may review our{' '}
                    <a
                      href="https://www.roomvu.com/privacy-policy"
                      target="_blank"
                      rel="noreferrer"
                      className={styles.privacyPolicy}
                    >
                      Privacy Policy
                    </a>{' '}
                    to learn how your data is used.
                  </Typography>
                </div>
              }
              onChange={() => setAgreementIsChecked(!agreementIsChecked)}
            />
            <Button
              className={styles.submitBtn}
              onClick={handleFormSubmit}
              disabled={isLoading || !agreementIsChecked}
              size="lg"
              color="secondary"
            >
              Send Message
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
